/**
 * iter238 — Dialogue « Partager le suivi ».
 * Crée / affiche / révoque le lien public de suivi des OR :
 *   - depuis une fiche client : lien permanent, extensible à plusieurs clients
 *   - depuis un OR : lien ponctuel pour ce dossier
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Check, Copy, Eye, EyeOff, Link2, Loader2, Lock, Mail, Plus, Search, Trash2, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// iter239 — Ce que le lien laisse voir. Un lien « chauffeurs » n'affiche que le statut.
const VIS_FIELDS = [
  { key: "devis", label: "Devis et montants" },
  { key: "photos", label: "Photos du véhicule" },
  { key: "pieces_jointes", label: "Pièces jointes partagées" },
  { key: "references", label: "Références du dossier" },
  { key: "autorisation", label: "Saisie du n° d'autorisation par le client" },
  { key: "termines", label: "Historique des dossiers terminés (12 derniers mois)" },
  { key: "messagerie", label: "Messagerie avec le client" },
];
const VIS_COMPLET = { devis: true, photos: true, pieces_jointes: true, references: true, autorisation: true, termines: true, messagerie: true };
const VIS_STATUT = { devis: false, photos: false, pieces_jointes: false, references: false, autorisation: false, termines: false, messagerie: false };

export default function SuiviShareDialog({ open, onClose, clientId, clientLabel, orId, orNumero }) {
  const scope = orId ? "or" : "clients";
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [allLinks, setAllLinks] = useState([]);   // plusieurs liens possibles pour un même client
  const [emails, setEmails] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [extraClients, setExtraClients] = useState([]);   // {id, nom}
  const [vis, setVis] = useState(VIS_COMPLET);
  const [label, setLabel] = useState("");
  const [vehiculesTiers, setVehiculesTiers] = useState([]);   // iter348

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = orId ? { or_id: orId } : { client_id: clientId };
      const { data } = await api.get("/suivi-tokens", { params });
      const items = data.items || [];
      const existing = items[0] || null;
      setAllLinks(items);
      setLink(existing);
      setEmails((existing?.notify_emails || []).join(", "));
      setVis(existing?.visibilite || VIS_COMPLET);
      setLabel(existing?.label || "");
      setExtraClients([]);
    } catch {
      setLink(null);
    } finally {
      setLoading(false);
    }
  }, [open, orId, clientId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults((data.clients || []).slice(0, 6));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // iter348 — Seuls les véhicules d'un AUTRE client peuvent être masqués du lien :
  // ils arrivent sur le lien par un dossier ouvert au nom du client du lien
  // (gestionnaire de flotte), et n'étaient donc jamais proposés jusqu'ici.
  const chargerTiers = useCallback(async () => {
    if (!open || scope !== "clients" || !link?.id) { setVehiculesTiers([]); return; }
    try {
      const { data } = await api.get(`/suivi-tokens/${link.id}/vehicules-tiers`);
      setVehiculesTiers(data.items || []);
    } catch { setVehiculesTiers([]); }
  }, [open, scope, link?.id]);

  useEffect(() => { chargerTiers(); }, [chargerTiers]);

  const masquerVehicule = async (vehicule_id, masquer) => {
    try {
      await api.post(`/suivi-tokens/${link.id}/masquer-vehicule`, { vehicule_id, masquer });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modification impossible");
      return;
    }
    toast.success(masquer ? "Véhicule masqué de ce lien" : "Véhicule de nouveau visible");
    // Le rafraîchissement du parent ne doit pas faire croire à un échec
    try { await onChange?.(); } catch { /* ignoré */ }
    await chargerTiers();
  };

  const cleanEmails = () => emails.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));

  const create = async () => {
    setBusy(true);
    try {
      const body = scope === "or"
        ? { scope: "or", or_id: orId, notify_emails: cleanEmails(), visibilite: vis, label: label.trim() || null }
        : {
            scope: "clients",
            client_ids: [clientId, ...extraClients.map((c) => c.id)],
            notify_emails: cleanEmails(),
            visibilite: vis,
            label: label.trim() || null,
          };
      const { data } = await api.post("/suivi-tokens", body);
      setLink(data);
      setAllLinks((prev) => [data, ...prev.filter((l) => l.id !== data.id)]);
      toast.success("Lien de suivi créé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally {
      setBusy(false);
    }
  };

  const save = async (patch) => {
    if (!link) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/suivi-tokens/${link.id}`, patch);
      setLink(data);
      setAllLinks((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      setEmails((data.notify_emails || []).join(", "));
      setVis(data.visibilite || VIS_COMPLET);
      setLabel(data.label || "");
      toast.success("Lien mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mise à jour impossible");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!link || !window.confirm("Révoquer ce lien ? Il ne fonctionnera plus pour le client.")) return;
    setBusy(true);
    try {
      await api.delete(`/suivi-tokens/${link.id}`);
      setAllLinks((prev) => prev.filter((l) => l.id !== link.id));
      setLink(null);
      toast.success("Lien révoqué");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Révocation impossible");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie impossible — sélectionnez le lien manuellement");
    }
  };

  const sendMail = async () => {
    const to = (sendTo || "").trim();
    if (!to.includes("@")) { toast.error("Adresse e-mail invalide"); return; }
    setBusy(true);
    try {
      await api.post(`/suivi-tokens/${link.id}/envoyer`, { email: to });
      toast.success(`Lien envoyé à ${to}`);
      setSendTo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const addClient = async (c) => {
    const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
    setQ(""); setResults([]);
    if (c.id === clientId || extraClients.some((x) => x.id === c.id)) return;
    if (link) {
      await save({ client_ids: [...(link.client_ids || []), c.id] });
    } else {
      setExtraClients((prev) => [...prev, { id: c.id, nom }]);
    }
  };

  const removeClient = async (cid) => {
    if (link) {
      const ids = (link.client_ids || []).filter((x) => x !== cid);
      if (ids.length === 0) { toast.error("Au moins un client est requis"); return; }
      await save({ client_ids: ids });
    } else {
      setExtraClients((prev) => prev.filter((x) => x.id !== cid));
    }
  };

  const clientIds = link ? (link.client_ids || []) : [clientId, ...extraClients.map((c) => c.id)];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="suivi-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#52a675]" />
            Partager le suivi
          </DialogTitle>
          <DialogDescription>
            {scope === "or"
              ? `Lien de suivi du dossier ${orNumero || ""} — le client voit l'avancement sans se connecter.`
              : `Lien permanent pour ${clientLabel || "ce client"} — tous ses véhicules à l'atelier, mis à jour en direct.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div className="space-y-4">
            {(allLinks.length > 1 || (link && scope === "clients")) && (
              <div className="flex flex-wrap gap-1.5 items-center" data-testid="suivi-share-link-switch">
                {allLinks.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      setLink(l);
                      setEmails((l.notify_emails || []).join(", "));
                      setVis(l.visibilite || VIS_COMPLET);
                      setLabel(l.label || "");
                    }}
                    className={`px-2 py-1 rounded-full border text-xs ${
                      l.id === link?.id
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}
                    data-testid={`suivi-share-link-${l.id}`}
                  >
                    {l.label || "Lien"}
                  </button>
                ))}
                {scope === "clients" && (
                  <button
                    type="button"
                    onClick={() => { setLink(null); setVis(VIS_COMPLET); setLabel(""); setEmails(""); }}
                    className="px-2 py-1 rounded-full border border-dashed border-gray-300 text-xs text-gray-500 hover:bg-gray-50 flex items-center gap-1"
                    data-testid="suivi-share-new-link"
                  >
                    <Plus className="w-3 h-3" /> Autre lien
                  </button>
                )}
              </div>
            )}
            {scope === "clients" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Clients inclus dans le lien
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="suivi-share-clients">
                  <span className="px-2 py-1 rounded-full bg-[#52a675]/10 text-[#3d8a5c] text-xs border border-[#52a675]/30">
                    {clientLabel}
                  </span>
                  {(link ? clientIds.filter((c) => c !== clientId) : extraClients.map((c) => c.id)).map((cid) => {
                    const nom = (link?.clients || []).find((c) => c.id === cid)?.nom
                      || extraClients.find((c) => c.id === cid)?.nom
                      || "Client ajouté";
                    return (
                      <span key={cid} className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200 flex items-center gap-1">
                        {nom}
                        <button type="button" onClick={() => removeClient(cid)} data-testid={`suivi-share-remove-${cid}`}>
                          <X className="w-3 h-3 text-gray-400 hover:text-red-600" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div className="relative mt-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ajouter un autre client au même lien…"
                    className="pl-9 h-9 text-sm"
                    data-testid="suivi-share-client-search"
                  />
                  {results.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                      {results.map((c) => {
                        const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => addClient(c)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                            data-testid={`suivi-share-client-option-${c.id}`}
                          >
                            <Plus className="w-3.5 h-3.5 text-[#52a675]" /> {nom}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Nom du lien
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ex. Helvetic Motion — dossier complet / Chauffeurs"
                  className="h-9 text-sm"
                  data-testid="suivi-share-label"
                />
                {link && (
                  <Button
                    variant="outline"
                    className="h-9"
                    disabled={busy}
                    onClick={() => save({ label })}
                    data-testid="suivi-share-label-save"
                  >
                    Enregistrer
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Visible par le client en haut de la page. Utile pour distinguer plusieurs liens du même client.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Ce que le client voit
                </Label>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => (link ? save({ visibilite: VIS_COMPLET }) : setVis(VIS_COMPLET))}
                    className="px-2 py-0.5 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
                    data-testid="suivi-share-preset-complet"
                  >
                    Dossier complet
                  </button>
                  <button
                    type="button"
                    onClick={() => (link ? save({ visibilite: VIS_STATUT }) : setVis(VIS_STATUT))}
                    className="px-2 py-0.5 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
                    data-testid="suivi-share-preset-statut"
                  >
                    Statut seul
                  </button>
                </div>
              </div>
              <div className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {VIS_FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="text-sm text-gray-700">{f.label}</span>
                    <Switch
                      checked={vis[f.key] !== false}
                      disabled={busy}
                      onCheckedChange={(v) => {
                        const next = { ...vis, [f.key]: v };
                        setVis(next);
                        if (link) save({ visibilite: next });
                      }}
                      data-testid={`suivi-share-vis-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Le statut et la progression sont toujours visibles. Tout décocher donne un lien « statut seul »,
                idéal pour les chauffeurs.
              </p>
            </div>

            {/* iter271 — Droits propres à chaque société du lien */}
            {link && scope === "clients" && (link.clients || []).length > 1 && (
              <div data-testid="suivi-share-vis-par-client">
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Droits par société
                </Label>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Chaque société peut avoir ses propres autorisations sur ce même lien.
                </p>
                {(link.clients || []).map((c) => {
                  const perso = (link.visibilite_par_client || {})[c.id] || {};
                  const effectif = { ...(link.visibilite || {}), ...perso };
                  return (
                    <div key={c.id} className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 text-sm font-semibold text-gray-800 flex items-center justify-between">
                        <span className="truncate">{c.nom}</span>
                        {Object.keys(perso).length > 0 && (
                          <button
                            type="button"
                            className="text-[11px] font-normal text-gray-500 underline"
                            onClick={() => {
                              const next = { ...(link.visibilite_par_client || {}) };
                              delete next[c.id];
                              save({ visibilite_par_client: next });
                            }}
                            data-testid={`suivi-share-vis-reset-${c.id}`}
                          >
                            Revenir aux droits du lien
                          </button>
                        )}
                      </div>
                      <div className="divide-y divide-gray-100">
                        {VIS_FIELDS.map((f) => (
                          <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-1.5">
                            <span className="text-[13px] text-gray-700">{f.label}</span>
                            <Switch
                              checked={effectif[f.key] !== false}
                              disabled={busy}
                              onCheckedChange={(v) => {
                                const next = {
                                  ...(link.visibilite_par_client || {}),
                                  [c.id]: { ...effectif, [f.key]: v },
                                };
                                save({ visibilite_par_client: next });
                              }}
                              data-testid={`suivi-share-vis-${c.id}-${f.key}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* iter272 — Mot de passe (liens flotte permanents uniquement) */}
            {link && scope === "clients" && (
              <div data-testid="suivi-share-mdp">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                      Accès par mot de passe
                    </Label>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Mot de passe générique à communiquer ; le client le remplace à sa première ouverture.
                    </p>
                  </div>
                  <Switch
                    checked={!!link.mdp_actif}
                    disabled={busy}
                    onCheckedChange={(v) => save({ mdp_actif: v })}
                    data-testid="suivi-share-mdp-switch"
                  />
                </div>
                {link.mdp_actif && (
                  <div className="mt-2 rounded-lg border border-gray-200 px-3 py-2 text-sm flex items-center gap-2">
                    <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    {link.mdp_initial ? (
                      <>
                        <span className="text-gray-600">Mot de passe à communiquer :</span>
                        <code className="font-bold tracking-wider" data-testid="suivi-share-mdp-valeur">
                          {link.mdp_initial}
                        </code>
                      </>
                    ) : (
                      <span className="text-gray-600" data-testid="suivi-share-mdp-perso">
                        Le client a défini son propre mot de passe
                        {link.mdp_change_at ? ` le ${new Date(link.mdp_change_at).toLocaleDateString("fr-CH")}` : ""}.
                      </span>
                    )}
                    <button
                      type="button"
                      className="ml-auto text-[11px] text-gray-500 underline flex items-center gap-1"
                      onClick={() => save({ mdp_reset: true })}
                      data-testid="suivi-share-mdp-reset"
                    >
                      <Unlock className="w-3 h-3" /> Réinitialiser
                    </button>
                  </div>
                )}
              </div>
            )}

            {scope === "clients" && link && vehiculesTiers.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Véhicules d&apos;un autre client visibles sur ce lien
                </Label>
                <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
                  Seuls les véhicules appartenant à un autre client peuvent être masqués ;
                  le masquage ne vaut que pour ce lien.
                </p>
                <div className="space-y-1" data-testid="suivi-share-tiers-list">
                  {vehiculesTiers.map((v) => {
                    const masque = v.masque;
                    return (
                      <div key={v.id} className="flex items-center gap-2 text-xs border border-gray-200 rounded px-2 py-1.5">
                        <span className="flex-1 truncate">
                          <span className="font-mono">{v.label}</span>
                          <span className="text-gray-500"> — {v.proprietaire}</span>
                        </span>
                        <Button
                          type="button" size="sm" variant="outline"
                          className={`text-[11px] h-7 ${masque ? "border-amber-300 text-amber-700" : ""}`}
                          onClick={() => masquerVehicule(v.id, !masque)}
                          data-testid={`suivi-share-toggle-${v.id}`}
                        >
                          {masque
                            ? <><EyeOff className="w-3 h-3 mr-1" /> Masqué</>
                            : <>Visible</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* iter293 — l'admin autorise ; c'est le destinataire qui règle ses notifications */}
            <div className="rounded-lg border border-gray-200 p-3">
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Notifications
              </Label>
              {link ? (
                <>
                  <label className="flex items-start gap-2 mt-2 text-sm text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!link.notifications_autorisees}
                      disabled={busy}
                      onChange={(e) => save({ notifications_autorisees: e.target.checked })}
                      className="accent-[#52a675] w-4 h-4 mt-0.5"
                      data-testid="suivi-share-notif-autorise"
                    />
                    <span>
                      Autoriser le destinataire à régler ses notifications
                      <span className="block text-[11px] text-gray-500">
                        Il choisit lui-même SMS et/ou e-mail, ses coordonnées et les étapes qui
                        l&apos;intéressent. Rien n&apos;est envoyé tant qu&apos;il n&apos;a rien réglé.
                      </span>
                    </span>
                  </label>
                  {Object.entries(link.notif_prefs || {}).filter(([, p]) => p?.sms || p?.email).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="suivi-share-notif-resume">
                      {Object.entries(link.notif_prefs || {}).filter(([, p]) => p?.sms || p?.email).map(([cid, p]) => (
                        <div key={cid} className="text-[11px] text-gray-600">
                          {(link.clients || []).find((c) => c.id === cid)?.nom || "Client"} :
                          {p.sms ? ` SMS ${p.tel}` : ""}{p.sms && p.email ? " ·" : ""}
                          {p.email ? ` e-mail ${p.email_addr}` : ""}
                          {" — "}
                          {Object.entries(p.etapes || {}).filter(([, v]) => v).length} étape(s)
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  À régler après la création du lien.
                </p>
              )}
            </div>

            {!link ? (
              <Button
                onClick={create}
                disabled={busy}
                className="w-full bg-[#52a675] hover:bg-[#3d8a5c]"
                data-testid="suivi-share-create"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Créer le lien de suivi
              </Button>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Lien à partager</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input readOnly value={link.url} className="h-9 text-xs font-mono" data-testid="suivi-share-url" />
                    <Button variant="outline" className="h-9 px-3" onClick={copy} data-testid="suivi-share-copy">
                      {copied ? <Check className="w-4 h-4 text-[#52a675]" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1" data-testid="suivi-share-vues">
                      <Eye className="w-3.5 h-3.5" /> {link.vues} consultation{link.vues === 1 ? "" : "s"}
                    </span>
                    {link.derniere_vue_at && (
                      <span>dernière le {new Date(link.derniere_vue_at).toLocaleDateString("fr-CH")}</span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Envoyer le lien</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      value={sendTo}
                      onChange={(e) => setSendTo(e.target.value)}
                      placeholder="contact@societe.ch"
                      className="h-9 text-sm"
                      data-testid="suivi-share-sendto"
                    />
                    <Button
                      variant="outline"
                      className="h-9"
                      disabled={busy}
                      onClick={sendMail}
                      data-testid="suivi-share-send"
                    >
                      <Mail className="w-4 h-4 mr-1" /> Envoyer
                    </Button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={revoke}
                  disabled={busy}
                  className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                  data-testid="suivi-share-revoke"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Révoquer ce lien
                </button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
